// ============================================================================
// Einladungsseite — /einladung/:token
// ============================================================================
// Eine Seite, ein Gast, zwei Knoepfe. Der Token im Link ist der Ausweis; es gibt
// hier bewusst keinen Login und keine Liste der anderen Gaeste.
// Eine Antwort laesst sich jederzeit aendern — jemand, der erst absagt und dann
// doch kann, soll nicht anrufen muessen.
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './EinladungPage.css';

function datumLang(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

export default function EinladungPage() {
  const { token } = useParams();
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [sendet, setSendet] = useState(false);
  const [personen, setPersonen] = useState(1);
  const [notiz, setNotiz] = useState('');
  const [gespeichert, setGespeichert] = useState(false);

  const laden = useCallback(async () => {
    try {
      const r = await fetch(`/api/einladung/${encodeURIComponent(token)}`);
      const d = await r.json();
      if (!r.ok || !d.success) {
        setFehler(d?.error || 'Diese Einladung konnte nicht geladen werden.');
      } else {
        setDaten(d.data);
        setPersonen(d.data.gast.personen || 1);
        setNotiz(d.data.gast.notiz || '');
      }
    } catch {
      setFehler('Keine Verbindung. Bitte später erneut versuchen.');
    } finally {
      setLaedt(false);
    }
  }, [token]);

  useEffect(() => { laden(); }, [laden]);

  const antworten = async (zusage) => {
    setSendet(true);
    setGespeichert(false);
    try {
      const r = await fetch(`/api/einladung/${encodeURIComponent(token)}/antwort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zusage, personen: zusage ? personen : null, notiz })
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setFehler(d?.error || 'Die Antwort konnte nicht gespeichert werden.');
      } else {
        setDaten(v => ({ ...v, gast: { ...v.gast, ...d.data, geantwortet_am: new Date().toISOString() } }));
        setGespeichert(true);
      }
    } catch {
      setFehler('Keine Verbindung. Bitte später erneut versuchen.');
    } finally {
      setSendet(false);
    }
  };

  if (laedt) {
    return <div className="einl-seite"><div className="einl-karte einl-karte--schmal"><p>Einladung wird geladen…</p></div></div>;
  }

  if (fehler && !daten) {
    return (
      <div className="einl-seite">
        <div className="einl-karte einl-karte--schmal">
          <h1>Einladung nicht gefunden</h1>
          <p className="einl-leise">{fehler}</p>
          <p className="einl-leise">
            Falls der Link aus einer E-Mail stammt, kopiere ihn bitte vollständig in die
            Adresszeile. Sonst melde dich einfach unter info@tda-intl.com.
          </p>
        </div>
      </div>
    );
  }

  const { gast, event } = daten;
  const beantwortet = gast.status !== 'offen';

  return (
    <div className="einl-seite">
      <div className="einl-karte">
        <span className="einl-marke">Tiger &amp; Dragon Association</span>
        <h1 className="einl-titel">{event.name}</h1>

        {event.abgesagt && (
          <div className="einl-hinweis einl-hinweis--warnung">
            Diese Veranstaltung wurde abgesagt. Eine Rückmeldung ist nicht mehr nötig.
          </div>
        )}

        <p className="einl-anrede">
          Hallo {gast.name}
          {gast.organisation ? <span className="einl-leise"> · {gast.organisation}</span> : null},
          <br />wir würden uns freuen, dich dabei zu haben.
        </p>

        <dl className="einl-fakten">
          <div>
            <dt>Wann</dt>
            <dd>{datumLang(event.datum)}{event.end_datum && event.end_datum !== event.datum ? ` bis ${datumLang(event.end_datum)}` : ''}</dd>
          </div>
          <div>
            <dt>Wo</dt>
            <dd>{event.ort}</dd>
          </div>
        </dl>

        {event.beschreibung && (
          <div className="einl-text">
            {event.beschreibung.split('\n').map((z, i) => (z.trim() ? <p key={i}>{z}</p> : <br key={i} />))}
          </div>
        )}

        {!event.abgesagt && (
          <div className="einl-antwort">
            {beantwortet && (
              <div className={`einl-hinweis ${gast.status === 'zugesagt' ? 'einl-hinweis--gut' : 'einl-hinweis--neutral'}`}>
                {gast.status === 'zugesagt'
                  ? `Du hast zugesagt${gast.personen > 1 ? ` — für ${gast.personen} Personen` : ''}. Danke!`
                  : 'Du hast abgesagt. Schade, vielleicht beim nächsten Mal.'}
                <span className="einl-leise"> Du kannst deine Antwort hier jederzeit ändern.</span>
              </div>
            )}

            <h2 className="einl-frage">{beantwortet ? 'Antwort ändern' : 'Kommst du?'}</h2>

            <label className="einl-feld">
              <span>Wie viele Personen kommt ihr?</span>
              <input
                type="number"
                min="1"
                max="20"
                value={personen}
                onChange={(e) => setPersonen(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
              />
            </label>

            <label className="einl-feld">
              <span>Möchtest du uns noch etwas mitgeben? (freiwillig)</span>
              <textarea
                rows={3}
                maxLength={1000}
                value={notiz}
                onChange={(e) => setNotiz(e.target.value)}
                placeholder="Zum Beispiel: komme etwas später, bringe jemanden mit …"
              />
            </label>

            <div className="einl-knoepfe">
              <button className="einl-knopf einl-knopf--ja" disabled={sendet} onClick={() => antworten(true)}>
                Ich bin dabei
              </button>
              <button className="einl-knopf einl-knopf--nein" disabled={sendet} onClick={() => antworten(false)}>
                Ich kann leider nicht
              </button>
            </div>

            {gespeichert && <p className="einl-gespeichert">Gespeichert. Danke für die Rückmeldung!</p>}
            {fehler && <p className="einl-fehler">{fehler}</p>}
          </div>
        )}

        <p className="einl-fuss">
          Fragen? Schreib uns an <a href="mailto:info@tda-intl.com">info@tda-intl.com</a>.
        </p>
      </div>
    </div>
  );
}
